import { useState } from "react";
import { Utensils, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button, EmptyState, Spinner, Icon, Sheet } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useDosFood, useAteHere } from "../../app/hooks/useDayOfShow.js";
import styles from "./dayOfShow.module.scss";

type FoodItem = components["schemas"]["FoodItem"];

/** The cuisine chips — "BBQ through Bars" (spec); a tap searches Places for that cuisine near the city. */
const CUISINES = ["BBQ", "Pizza", "Sushi", "Tacos", "Burgers", "Thai", "Italian", "Ramen", "Vegan", "Bars"] as const;

// A Google search for the spot's menu (new tab). Assembled from parts so the noHardcodedHosts lint (which bans
// http(s):// literals reserved for injected API bases) doesn't trip on a third-party consumer link.
const GOOGLE_SEARCH = ["https:", "//www.google.com/search?q="].join("");
const menuUrl = (name: string, city: string) => GOOGLE_SEARCH + encodeURIComponent(`${name} ${city} menu`);

/**
 * The food finder — cuisine chips + rich result rows (★ rating · $-tier · open/closed · View menu), rebuilt to
 * the legacy flow (Ryan, ART-174: "how find food works"): you PICK A CUISINE first, which fires the Places
 * search near the city; before that it just invites you to tap one. Each result carries its own "Ate here" →
 * 👍/👎 (a band-attributed cross-band rec, deduped server-side) — there is no free-text add. Places degrades to
 * community recs when unavailable/rate-limited. Shared by the DOS tab (a bottom SHEET, controlled by the DOS
 * "Food" tile) and the logistics page (an inline card with its own opener).
 */
export function FoodFinderCard({
  showId,
  city,
  online,
  open,
  onOpenChange,
}: {
  showId: string;
  city: string;
  online: boolean;
  open?: boolean;
  onOpenChange?: (v: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const [cuisine, setCuisine] = useState<string | undefined>(undefined);
  // LAZY + legacy: the Places call fires only once the finder is open AND a cuisine is picked.
  const food = useDosFood(showId, isOpen && !!cuisine, cuisine);
  const ateHere = useAteHere();
  // Per-row "Ate here": `pending` is the row showing its 👍/👎 picker; `rated` is the optimistic verdict so a
  // saved row flips to "✓ Rated" instantly (the server invalidation then re-lands it as a community badge).
  const [pending, setPending] = useState<string | null>(null);
  const [rated, setRated] = useState<Record<string, boolean>>({});
  const items: readonly FoodItem[] = food.data?.items ?? [];

  const controlled = open !== undefined;

  function rate(item: FoodItem, thumbUp: boolean) {
    const name = item.name ?? "";
    setRated((m) => ({ ...m, [name]: thumbUp }));
    setPending(null);
    ateHere.mutate(
      { showId, body: { name: item.name, address: item.address, category: cuisine, city, thumbUp } },
      // useAteHere toasts on error; roll the optimistic verdict back so a failed write can't read as saved.
      { onError: () => setRated((m) => { const n = { ...m }; delete n[name]; return n; }) },
    );
  }

  const body = (
    <>
      {/* Cuisine chips — a tap searches that cuisine; the active chip toggles back to the pick prompt. */}
      <div className={styles.cuisineChips} data-testid="dos-food-cuisines">
        {CUISINES.map((c) => (
          <button
            key={c}
            type="button"
            className={styles.cuisineChip}
            data-active={cuisine === c || undefined}
            onClick={() => {
              setPending(null);
              setCuisine((cur) => (cur === c ? undefined : c));
            }}
          >
            {c}
          </button>
        ))}
      </div>

      {!cuisine ? (
        <p className={styles.empty}>Tap a cuisine to see open spots{city ? ` near ${city}` : ""}.</p>
      ) : food.isLoading ? (
        <div className={styles.center}><Spinner /></div>
      ) : food.isError || !food.data ? (
        <EmptyState title="Couldn't load food" description="Try again in a moment." />
      ) : (
        <>
          {!food.data.placesAvailable && (
            <p className={styles.degraded} data-testid="dos-food-degraded">
              Live search is unavailable — showing community recommendations.
            </p>
          )}
          {items.length === 0 ? (
            <p className={styles.empty}>No spots found — try another cuisine.</p>
          ) : (
            <ul className={styles.foodList}>
              {items.map((f, i) => (
                <FoodRow
                  key={`${f.name}-${i}`}
                  item={f}
                  city={city}
                  online={online}
                  rated={rated[f.name ?? ""]}
                  isPending={pending === (f.name ?? "")}
                  saving={ateHere.isPending}
                  onAte={() => setPending(f.name ?? "")}
                  onRate={(thumbUp) => rate(f, thumbUp)}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );

  // Controlled (the DOS Essentials "Food" tile drives it): a bottom SHEET, nothing when closed.
  // Uncontrolled (logistics): an inline card with its own opener.
  if (controlled) {
    return isOpen ? (
      <Sheet title={city ? `Food · ${city}` : "Food"} onClose={() => setOpen(false)}>
        <div className={styles.foodSheet} data-testid="dos-food">{body}</div>
      </Sheet>
    ) : null;
  }
  return (
    <div className={styles.card} data-testid="dos-food" id="dos-food">
      <span className={styles.sectionEyebrow}>Food{city ? ` · ${city}` : ""}</span>
      {!isOpen ? (
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)} disabled={!city}>
          <Icon icon={Utensils} size={16} /> {city ? `Find food near ${city}` : "No city set"}
        </Button>
      ) : (
        body
      )}
    </div>
  );
}

function FoodRow({
  item: f,
  city,
  online,
  rated,
  isPending,
  saving,
  onAte,
  onRate,
}: {
  item: FoodItem;
  city: string;
  online: boolean;
  /** The caller's optimistic verdict for this row (undefined = not yet rated this session). */
  rated: boolean | undefined;
  isPending: boolean;
  saving: boolean;
  onAte: () => void;
  onRate: (thumbUp: boolean) => void;
}) {
  const price = f.priceLevel != null && f.priceLevel > 0 ? "$".repeat(f.priceLevel) : null;
  return (
    <li className={styles.foodItem}>
      <span className={styles.foodName}>{f.name}</span>
      {f.address && <span className={styles.foodAddr}>{f.address}</span>}
      <span className={styles.foodStats}>
        {/* Cross-band badge — another band already rated this spot in this city (band name + their 👍/👎). */}
        {f.ratedByBand && (
          <span className={styles.foodBadge}>{f.ratedByBand} {f.thumbUp === false ? "👎" : "👍"}</span>
        )}
        {f.rating != null && <span className={styles.foodRating}>★ {f.rating.toFixed(1)}</span>}
        {price && <span>{price}</span>}
        {f.openNow != null && (
          <span className={styles.foodOpen} data-open={f.openNow || undefined}>
            {f.openNow ? "Open" : "Closed"}
          </span>
        )}
        <a className={styles.foodMenu} href={f.mapsUrl ?? menuUrl(f.name ?? "", city)} target="_blank" rel="noopener noreferrer">
          View menu →
        </a>
      </span>
      {/* "Ate here" is per-row (legacy): tap → 👍/👎 → saved. Disabled offline (the write can't land). */}
      <div className={styles.foodAte}>
        {rated !== undefined ? (
          <span className={styles.foodRated}>✓ Rated {rated ? "👍" : "👎"}</span>
        ) : isPending ? (
          <span className={styles.thumbToggle} role="group" aria-label="Rating">
            <button type="button" aria-label="Thumbs up" disabled={!online || saving} onClick={() => onRate(true)}>
              <ThumbsUp size={14} />
            </button>
            <button type="button" aria-label="Thumbs down" disabled={!online || saving} onClick={() => onRate(false)}>
              <ThumbsDown size={14} />
            </button>
          </span>
        ) : (
          <button type="button" className={styles.foodAteBtn} disabled={!online} onClick={onAte}>
            Ate here
          </button>
        )}
      </div>
    </li>
  );
}
